'use client';

import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { cn } from '@/hen-ui/utils';
import { outsideCloseVariants } from '@/hen-ui/lib/outside-close.variants';
import { usePortalContainer } from '@/hen-ui/providers/PortalContainerContext';

/** Fired on the dialog content after it handles open focus, so content can scroll and focus once that is done. */
export const DIALOG_OPENED_EVENT = 'edu-dialog-opened';

const CONTENT_FOCUS_SELECTOR =
  'a[href], button:not([data-slot="dialog-close"]), input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

/** First control in the dialog that is not its close button. */
function focusFirstContentControl(root: EventTarget | null) {
  if (!(root instanceof HTMLElement)) return;
  const candidate = Array.from(
    root.querySelectorAll<HTMLElement>(CONTENT_FOCUS_SELECTOR),
  ).find((element) => element.closest('[data-slot="dialog-close"]') === null);
  candidate?.focus();
}

/**
 * Preview-only. A live embed that portals into `[data-de-portal]` in this
 * document, without a `[data-de-preview]` ancestor, stays on the Radix path
 * (focus trap and Radix overlay). The plain overlay and disabled auto-focus
 * apply in the authoring preview iframe and under `[data-de-preview]`.
 */
export function isContainedPreviewPortal(
  container: HTMLElement | undefined,
): boolean {
  if (!container) return false;
  if (typeof document !== 'undefined' && container.ownerDocument !== document) {
    return true;
  }
  return container.closest('[data-de-preview]') !== null;
}

interface ContainedDialogState {
  open: boolean;
  blockPage: boolean;
  setOpen: (open: boolean) => void;
  /** Element that had focus when the dialog opened; focus returns here on close. */
  openerRef: React.MutableRefObject<FocusableOpener | null>;
}

const ContainedDialogContext = React.createContext<ContainedDialogState | null>(
  null,
);

interface ScrollLockRecord {
  count: number;
  overflowX: string;
  overflowY: string;
}

const previewScrollLocks = new WeakMap<HTMLElement, ScrollLockRecord>();

function lockPreviewScroll(container: HTMLElement): () => void {
  const scrollRoot = container.ownerDocument.getElementById('preview-root');
  if (!scrollRoot) return () => undefined;

  const existing = previewScrollLocks.get(scrollRoot);
  if (existing) {
    existing.count += 1;
  } else {
    previewScrollLocks.set(scrollRoot, {
      count: 1,
      overflowX: scrollRoot.style.overflowX,
      overflowY: scrollRoot.style.overflowY,
    });
    scrollRoot.style.overflowX = 'hidden';
    scrollRoot.style.overflowY = 'hidden';
  }

  return () => {
    const current = previewScrollLocks.get(scrollRoot);
    if (!current) return;
    current.count -= 1;
    if (current.count > 0) return;
    scrollRoot.style.overflowX = current.overflowX;
    scrollRoot.style.overflowY = current.overflowY;
    previewScrollLocks.delete(scrollRoot);
  };
}

const PRESSABLE_SELECTOR =
  'a[href], button, input, select, textarea, summary, [tabindex]:not([tabindex="-1"])';

let pressTrackingUsers = 0;
/** HTML or SVG: a bound Icon is an `<svg role="button">`. */
type FocusableOpener = HTMLElement | SVGElement;

function isOpenerElement(node: unknown): node is FocusableOpener {
  return node instanceof HTMLElement || node instanceof SVGElement;
}

let pendingPress: FocusableOpener | null = null;
let clickedPress: FocusableOpener | null = null;

function onTrackedPointerDown(event: PointerEvent) {
  // composedPath reaches inside shadow roots, where event.target is the host.
  pendingPress =
    (event
      .composedPath()
      .find(
        (node) => isOpenerElement(node) && node.matches(PRESSABLE_SELECTOR),
      ) as FocusableOpener | undefined) ?? null;
}

function onTrackedPointerCancel() {
  pendingPress = null;
}

function onTrackedClick(event: MouseEvent) {
  clickedPress =
    pendingPress && event.composedPath().includes(pendingPress)
      ? pendingPress
      : null;
  pendingPress = null;
  setTimeout(() => {
    clickedPress = null;
  }, 0);
}

/**
 * Tracks the control pressed during the current click, because Safari does
 * not focus tapped buttons. Every mounted Dialog shares one set of listeners.
 */
function usePressTracking(): void {
  React.useEffect(() => {
    if (pressTrackingUsers === 0) {
      document.addEventListener('pointerdown', onTrackedPointerDown, true);
      document.addEventListener('pointercancel', onTrackedPointerCancel, true);
      document.addEventListener('click', onTrackedClick, true);
    }
    pressTrackingUsers += 1;
    return () => {
      pressTrackingUsers -= 1;
      if (pressTrackingUsers > 0) return;
      document.removeEventListener('pointerdown', onTrackedPointerDown, true);
      document.removeEventListener(
        'pointercancel',
        onTrackedPointerCancel,
        true,
      );
      document.removeEventListener('click', onTrackedClick, true);
      pendingPress = null;
      clickedPress = null;
    };
  }, []);
}

/** Focused element, followed into open shadow roots. */
function deepActiveElement(doc: Document): Element | null {
  let active = doc.activeElement;
  while (active?.shadowRoot?.activeElement) {
    active = active.shadowRoot.activeElement;
  }
  return active;
}

/**
 * True when the dialog held focus as it closed and focus has not moved
 * anywhere since, so the document is left focused on its body.
 */
function focusLeftWithDialog(
  portalContainer: HTMLElement | undefined,
  focusWasInside: boolean,
): boolean {
  const doc = portalContainer?.ownerDocument;
  if (!focusWasInside || !doc?.hasFocus()) return false;
  const active = deepActiveElement(doc);
  return active == null || active === doc.body;
}

function isFocusableOpener(
  element: Element | null,
): element is FocusableOpener {
  return (
    isOpenerElement(element) &&
    element.isConnected &&
    element.tabIndex >= 0 &&
    !(element as HTMLButtonElement).disabled
  );
}

function Dialog({
  open,
  defaultOpen,
  onOpenChange,
  modal,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  const portalContainer = usePortalContainer();
  const contained = isContainedPreviewPortal(portalContainer);
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(
    defaultOpen ?? false,
  );
  const resolvedOpen = open ?? uncontrolledOpen;
  const blockPage = modal !== false;
  const openerRef = React.useRef<FocusableOpener | null>(null);
  const wasOpenRef = React.useRef(resolvedOpen);
  usePressTracking();

  const setOpen = React.useCallback(
    (nextOpen: boolean) => {
      if (open === undefined) setUncontrolledOpen(nextOpen);
      onOpenChange?.(nextOpen);
    },
    [onOpenChange, open],
  );

  React.useLayoutEffect(() => {
    if (!contained || !resolvedOpen || !blockPage || !portalContainer) {
      return;
    }
    return lockPreviewScroll(portalContainer);
  }, [blockPage, contained, portalContainer, resolvedOpen]);

  // Records the opener on a closed-to-open change, before Radix moves focus in.
  React.useLayoutEffect(() => {
    const wasOpen = wasOpenRef.current;
    wasOpenRef.current = resolvedOpen;
    if (!resolvedOpen || wasOpen || typeof document === 'undefined') return;
    const active = deepActiveElement(document);
    openerRef.current = isFocusableOpener(active) ? active : clickedPress;
  }, [resolvedOpen]);

  return (
    <ContainedDialogContext.Provider
      value={{ open: resolvedOpen, blockPage, setOpen, openerRef }}
    >
      <DialogPrimitive.Root
        data-slot='dialog'
        {...props}
        open={resolvedOpen}
        onOpenChange={setOpen}
        modal={contained ? false : modal}
      />
    </ContainedDialogContext.Provider>
  );
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot='dialog-trigger' {...props} />;
}

function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  const portalContainer = usePortalContainer();
  const markerRef = React.useRef<HTMLSpanElement>(null);
  const [ownerBody, setOwnerBody] = React.useState<HTMLElement | undefined>();

  React.useLayoutEffect(() => {
    setOwnerBody(markerRef.current?.ownerDocument?.body ?? undefined);
  }, []);

  const container = portalContainer ?? ownerBody;

  return (
    <>
      <span ref={markerRef} hidden data-slot='dialog-portal-marker' />
      {container ? (
        <DialogPrimitive.Portal
          data-slot='dialog-portal'
          {...props}
          container={container}
        />
      ) : null}
    </>
  );
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot='dialog-close' {...props} />;
}

// Shared enter/exit pair: open slower so entrance can be read; close snappier.
// Reuses `--motion-duration-open` / `--motion-duration-close` (any overlay can).
const ENTER_EXIT_MOTION =
  '[--motion-duration:var(--motion-duration-open,500ms)] data-[state=closed]:[--motion-duration:var(--motion-duration-close,250ms)] [--motion-easing:var(--motion-easing-default)]';

type DialogBackdrop = 'default' | 'tinted';

const TINTED_BACKDROP_CLASS = 'bg-overlay-tinted';
/** 40% black with the alpha in the color, so opacity keyframes can still fade it. */
const DIM_BACKDROP_CLASS = 'bg-overlay-dim';
// `bg-overlay-default` (not `bg-black/40`): Tailwind can't apply an opacity
// modifier to a color defined as a bare CSS var, so `bg-black/40` compiles
// to nothing here. The 40% dim must live in the color itself.
const DEFAULT_BACKDROP_CLASS = 'bg-overlay-default';

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & {
    /** Match the sheet layout's slower entrance instead of the quick fade. */
    slowMotion?: boolean;
    /** `tinted` dims with the theme's dark neutral instead of black. */
    backdrop?: DialogBackdrop | null;
    /**
     * Keeps the default dim at 40% black whatever the theme's
     * `--overlay-default`, for a close button that sits on the backdrop.
     */
    darkDim?: boolean;
  }
>(function DialogOverlay(
  {
    className,
    onClick,
    slowMotion = false,
    backdrop,
    darkDim = false,
    ...props
  },
  ref,
) {
  const portalContainer = usePortalContainer();
  const contained = isContainedPreviewPortal(portalContainer);
  const containedState = React.useContext(ContainedDialogContext);
  const motionClassName = slowMotion
    ? `${ENTER_EXIT_MOTION} data-[state=open]:animate-fade-in-motion data-[state=closed]:animate-fade-out-motion`
    : 'data-[state=open]:animate-fade-in-0 data-[state=closed]:animate-fade-out-0';
  // The 40% dim lives in the color, not `opacity`: an `!important` opacity
  // outranks the fade keyframes and leaves the backdrop fully dim on close.
  const dimClassName =
    backdrop === 'tinted'
      ? TINTED_BACKDROP_CLASS
      : darkDim
        ? DIM_BACKDROP_CLASS
        : DEFAULT_BACKDROP_CLASS;

  if (contained) {
    if (!containedState?.open || !containedState.blockPage) return null;
    return (
      <div
        ref={ref}
        data-slot='dialog-overlay'
        data-state='open'
        aria-hidden='true'
        className={cn(
          'absolute inset-0 z-modal',
          'flex items-center justify-center',
          dimClassName,
          slowMotion
            ? `${ENTER_EXIT_MOTION} animate-fade-in-motion`
            : 'animate-fade-in-0',
          'motion-reduce:animate-none',
          className,
        )}
        onClick={(event) => {
          onClick?.(event);
          if (!event.defaultPrevented) containedState.setOpen(false);
        }}
        {...props}
      />
    );
  }

  return (
    <DialogPrimitive.Overlay
      ref={ref}
      data-slot='dialog-overlay'
      className={cn(
        'fixed inset-0 z-modal',
        'flex items-center justify-center',
        dimClassName,
        motionClassName,
        'motion-reduce:animate-none',
        className,
      )}
      {...props}
    />
  );
});

type DialogSurface = 'default' | 'dark';
type DialogCloseButtonVariant = 'plain' | 'circle';
type DialogContentVariant = 'default' | 'fullscreen' | 'side';
type DialogPanelSide = 'left' | 'right';
type DialogClosePlacement = 'header' | 'outside';

function sidePanelClassName(side: DialogPanelSide): string {
  // Full viewport height, docked to the edge. Width comes from `max-w-*` on
  // the content root; authors set that via Modal `size`.
  return cn(
    'top-0 bottom-0 h-full max-h-none w-full rounded-none shadow-md',
    side === 'left' ? 'left-0' : 'right-0',
    ENTER_EXIT_MOTION,
    side === 'left'
      ? 'data-[state=open]:animate-panel-in-from-left data-[state=closed]:animate-panel-out-to-left'
      : 'data-[state=open]:animate-panel-in-from-right data-[state=closed]:animate-panel-out-to-right',
  );
}

/** Color and hover treatment of a close control; shared with the Modal sheet. */
function dialogCloseToneClassName(
  variant: DialogCloseButtonVariant,
  dark: boolean,
): string {
  if (variant === 'circle') {
    return cn('group', dark ? 'text-white' : 'text-heading');
  }
  return dark
    ? 'text-white opacity-80 transition-opacity hover:opacity-100'
    : 'text-neutral-700 opacity-70 transition-opacity hover:opacity-100';
}

/** The X, inside a filled circle for `circle`. Pair with `dialogCloseToneClassName`. */
function DialogCloseGlyph({
  variant,
  dark,
}: {
  variant: DialogCloseButtonVariant;
  dark: boolean;
}) {
  if (variant !== 'circle') return <X className='size-4' aria-hidden />;
  return (
    <span
      data-slot='dialog-close-surface'
      className={cn(
        'inline-flex size-8 items-center justify-center rounded-max transition-colors motion-reduce:transition-none',
        dark
          ? 'bg-neutral-700 group-hover:bg-neutral-500'
          : 'bg-neutral-100 group-hover:bg-neutral-200',
      )}
    >
      <X className='size-4' aria-hidden />
    </span>
  );
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  variant = 'default',
  side = 'right',
  surface = 'default',
  closeButtonVariant,
  closePlacement = 'header',
  headerTitle,
  headerClassName,
  bodyClassName,
  backdrop,
  capBodyHeight = false,
  onPointerDownOutside,
  onInteractOutside,
  onOpenAutoFocus,
  onCloseAutoFocus,
  onFocus,
  onBlur,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean;
  variant?: DialogContentVariant | null;
  /** Edge the sheet is attached to. Used when `variant` is `side`. */
  side?: DialogPanelSide | null;
  /** Paints the whole dialog. `dark` re-points text roles to on-dark colors. */
  surface?: DialogSurface | null;
  /** Unset keeps the plain X on `default` and the circle on `fullscreen`. */
  closeButtonVariant?: DialogCloseButtonVariant | null;
  /**
   * Side panel only: `outside` moves the close onto the backdrop past the
   * panel's open edge, out of the title row. Unset keeps it in the panel.
   */
  closePlacement?: DialogClosePlacement | null;
  /** Title in a pinned header row with the close control; names the dialog. */
  headerTitle?: React.ReactNode;
  /** Padding / spacing classes for the sticky title row. Unset keeps `p-md`. */
  headerClassName?: string;
  /** When set, wraps `children` in a body element carrying these classes. */
  bodyClassName?: string;
  /** Page dim behind the dialog. Fullscreen has no visible backdrop either way. */
  backdrop?: DialogBackdrop | null;
  /** Centered dialog only: scrolls the body past `--max-height-modal-body`; the title row and close stay put. */
  capBodyHeight?: boolean;
}) {
  const portalContainer = usePortalContainer();
  const contained = isContainedPreviewPortal(portalContainer);
  const containedState = React.useContext(ContainedDialogContext);
  const leftForPageRef = React.useRef(false);
  const focusInsideRef = React.useRef(false);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const isDark = surface === 'dark';
  const hasHeaderRow = headerTitle != null && headerTitle !== '';
  const capsBody =
    capBodyHeight && variant !== 'fullscreen' && variant !== 'side';
  const closeVariant =
    closeButtonVariant ?? (variant === 'fullscreen' ? 'circle' : 'plain');
  const panelSide: DialogPanelSide = side === 'left' ? 'left' : 'right';
  const closesOutside = variant === 'side' && closePlacement === 'outside';

  // A child of the panel, outside the clipped scroller, so it keeps the
  // dialog's focus order and slides with the panel.
  const outsideClose =
    showCloseButton && closesOutside ? (
      <DialogPrimitive.Close
        data-slot='dialog-close'
        className={outsideCloseVariants({
          side: panelSide,
          header: 'dialogTitle',
        })}
      >
        <X aria-hidden />
        <span className='sr-only'>Close</span>
      </DialogPrimitive.Close>
    ) : null;

  const showsPanelClose = showCloseButton && !closesOutside;

  const closeControl = showsPanelClose ? (
    <DialogPrimitive.Close
      data-slot='dialog-close'
      className={cn(
        hasHeaderRow
          ? // Keeps the 44px target without making the header row taller.
            'relative shrink-0 -my-1.5 -mr-1.5'
          : cn(
              'absolute right-lg',
              // Pinned to the panel over a capped scroller, so it must stay above scrolled content.
              capsBody && 'z-40',
              // Centered: the 44px target centers on the Modal.Header title line.
              variant === 'fullscreen' || variant === 'side'
                ? 'top-md'
                : 'top-xxs',
            ),
        // Target stays 44px even where the visible control is smaller.
        'inline-flex min-h-11 min-w-11 items-center justify-center rounded-max p-1',
        dialogCloseToneClassName(closeVariant, isDark),
        'motion-reduce:transition-none',
        // Radix focuses the close on open; show the ring for keyboard focus only.
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-focus',
        'disabled:pointer-events-none',
      )}
    >
      <DialogCloseGlyph variant={closeVariant} dark={isDark} />
      <span className='sr-only'>Close</span>
    </DialogPrimitive.Close>
  ) : null;

  const content = bodyClassName ? (
    <div
      data-slot='dialog-body'
      className={cn(
        variant === 'fullscreen' && 'flex flex-1 flex-col',
        bodyClassName,
      )}
    >
      {children}
    </div>
  ) : (
    children
  );

  const titleRow = hasHeaderRow ? (
    <div
      data-slot='dialog-title-row'
      className={cn(
        'sticky top-0 z-40 flex shrink-0 items-center justify-between gap-xs',
        headerClassName ?? 'p-md',
        isDark ? 'bg-surface-chrome' : 'bg-white',
      )}
    >
      <DialogTitle className='min-w-0 flex-1 text-h4'>
        {headerTitle}
      </DialogTitle>
      {closeControl}
    </div>
  ) : null;

  const body = hasHeaderRow ? (
    <>
      {titleRow}
      {content}
    </>
  ) : (
    <>
      {content}
      {closeControl}
    </>
  );

  return (
    <DialogPortal>
      <DialogOverlay
        slowMotion={variant === 'side'}
        darkDim={closesOutside}
        backdrop={backdrop}
        className={cn(variant === 'fullscreen' && 'bg-transparent')}
      />
      <DialogPrimitive.Content
        data-slot='dialog-content'
        data-surface={isDark ? 'dark' : undefined}
        data-panel-side={variant === 'side' ? panelSide : undefined}
        className={cn(
          contained ? 'absolute z-modal' : 'fixed z-modal',
          'flex w-full max-w-lg flex-col overflow-hidden',
          closesOutside && 'overflow-visible',
          isDark ? 'surface-dark' : 'bg-white',
          'p-0',
          variant === 'fullscreen'
            ? cn(
                'inset-0 h-full max-h-none max-w-none rounded-none shadow-none',
                '[--motion-duration:var(--motion-duration-slower,450ms)]',
                'duration-motion ease-motion',
                'data-[state=open]:animate-fade-in-motion',
                'data-[state=closed]:animate-fade-out-motion',
              )
            : variant === 'side'
              ? sidePanelClassName(panelSide)
              : cn(
                  'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
                  'w-[calc(100%-2rem)] rounded-md shadow-md',
                  contained
                    ? 'max-h-[calc(100%-2rem)]'
                    : 'max-h-[calc(100vh-2rem)]',
                  'duration-fast',
                  'data-[state=open]:animate-fade-in-0',
                  'data-[state=closed]:animate-fade-out-0',
                ),
          'motion-reduce:animate-none motion-reduce:transition-none',
          className,
        )}
        onPointerDownOutside={(event) => {
          if (contained) {
            const target = event.target as Node | undefined;
            if (
              portalContainer &&
              target &&
              target.ownerDocument !== portalContainer.ownerDocument
            ) {
              event.preventDefault();
            }
          }
          onPointerDownOutside?.(event);
        }}
        onInteractOutside={(event) => {
          onInteractOutside?.(event);
          // A non-modal dialog dismissed by using the page keeps focus there.
          if (!event.defaultPrevented && containedState?.blockPage === false) {
            leftForPageRef.current = true;
          }
        }}
        onFocus={(event) => {
          focusInsideRef.current = true;
          onFocus?.(event);
          // With nothing focusable inside, focus lands on the panel; hand it to
          // the scroll area so arrow and page keys still scroll the dialog.
          if (!event.defaultPrevented && event.target === event.currentTarget) {
            scrollRef.current?.focus({ preventScroll: true });
          }
        }}
        onBlur={(event) => {
          const next = event.relatedTarget;
          if (next && !event.currentTarget.contains(next)) {
            focusInsideRef.current = false;
          }
          onBlur?.(event);
        }}
        onOpenAutoFocus={(event) => {
          leftForPageRef.current = false;
          focusInsideRef.current = false;
          if (contained) event.preventDefault();
          onOpenAutoFocus?.(event);
          // A fullscreen dialog with no title draws the close before the
          // content, so the default open focus lands on the X. Start on the
          // first content control instead. Content can still move focus from
          // the open event, for example onto a chosen photo.
          if (
            !event.defaultPrevented &&
            variant === 'fullscreen' &&
            !hasHeaderRow
          ) {
            event.preventDefault();
            focusFirstContentControl(event.currentTarget);
          }
          const content = event.currentTarget;
          if (content) content.dispatchEvent(new Event(DIALOG_OPENED_EVENT));
        }}
        onCloseAutoFocus={(event) => {
          onCloseAutoFocus?.(event);
          const handled = event.defaultPrevented || leftForPageRef.current;
          if (contained) event.preventDefault();
          if (handled) return;
          // Preview never takes focus from the editor: it only returns focus
          // when focus was in the dialog and nothing else has it now.
          if (
            contained &&
            !focusLeftWithDialog(portalContainer, focusInsideRef.current)
          ) {
            return;
          }
          const opener = containedState?.openerRef.current ?? null;
          if (isFocusableOpener(opener)) {
            event.preventDefault();
            opener.focus({ preventScroll: contained });
          }
        }}
        {...props}
      >
        {outsideClose}
        {capsBody ? titleRow : null}
        {/* Scrolls inside the clipped panel so the scrollbar keeps the rounded corners. */}
        <div
          ref={scrollRef}
          data-slot='dialog-scroll'
          // A capped scroller is a sibling of Close, so it needs its own tab stop for keyboard scrolling.
          tabIndex={capsBody ? 0 : -1}
          role={capsBody ? 'region' : undefined}
          aria-label={capsBody ? 'Dialog content' : undefined}
          className={cn(
            'relative flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden outline-none',
            capsBody &&
              'max-h-modal-body focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus',
          )}
        >
          {variant === 'fullscreen' ? (
            <div
              data-slot='dialog-fullscreen-column'
              className={cn(
                'relative mx-auto flex w-full max-w-modal-fullscreen flex-1 flex-col',
                // Header text must clear the close control: right-lg offset plus its 44px target.
                '[&_[data-slot=dialog-header]]:pr-[4.5rem]',
                '[&_[data-slot=dialog-title]]:text-h4',
                // Media fills the width edge to edge, so the title needs its own
                // surface to stay legible once content scrolls beneath it.
                '[&_[data-slot=dialog-header]]:sticky [&_[data-slot=dialog-header]]:top-0',
                '[&_[data-slot=dialog-header]]:z-30',
                isDark
                  ? '[&_[data-slot=dialog-header]]:bg-surface-chrome'
                  : '[&_[data-slot=dialog-header]]:bg-white',
              )}
            >
              {hasHeaderRow ? (
                body
              ) : (
                <>
                  {/* Zero-height so the close rides the top of the scroll viewport
                      with the title instead of scrolling away with the media. */}
                  <div
                    data-slot='dialog-fullscreen-close-bar'
                    className='sticky top-0 z-40 h-0'
                  >
                    {closeControl}
                  </div>
                  {content}
                </>
              )}
            </div>
          ) : capsBody ? (
            content
          ) : (
            body
          )}
        </div>
        {capsBody && !hasHeaderRow ? closeControl : null}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='dialog-header'
      className={cn(
        'flex flex-col gap-1',
        'px-lg pt-md pb-md',
        'border-b border-neutral-100',
        className,
      )}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='dialog-footer'
      className={cn(
        'flex flex-col-reverse sm:flex-row sm:justify-end gap-xs',
        'px-lg py-md',
        'border-t border-neutral-100',
        className,
      )}
      {...props}
    />
  );
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot='dialog-title'
      className={cn('text-h6 font-bold font-heading text-heading', className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot='dialog-description'
      className={cn('text-b5 text-subtext', className)}
      {...props}
    />
  );
}

export {
  DialogCloseGlyph,
  dialogCloseToneClassName,
  DIM_BACKDROP_CLASS,
  TINTED_BACKDROP_CLASS,
  DEFAULT_BACKDROP_CLASS,
  type DialogBackdrop,
  type DialogCloseButtonVariant,
  type DialogClosePlacement,
  type DialogSurface,
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
